<template>
  <div v-loading.fullscreen.lock="display.isLoading">
    <div
      v-if="
        shoppingCart.shoppingCart.lineItems &&
        shoppingCart.shoppingCart.lineItems.length <= 0
      "
    >
      <h3 class="centre">Shopping cart is empty.</h3>
    </div>
    <div v-else>
      <h3 class="centre">Shopping Cart</h3>
      <el-row class="line-item line-item-header">
        <el-col :span="2" class="centre">
          <b>#</b>
        </el-col>
        <el-col :span="6" class="centre">
          <b>Product</b>
        </el-col>
        <el-col :span="4" class="centre">
          <b>Unit Price</b>
        </el-col>
        <el-col :span="8" class="centre">
          <b>Qty</b>
        </el-col>
        <el-col :span="4" class="centre">
          <b>Total</b>
        </el-col>
      </el-row>
      <el-row
        type="flex"
        align="middle"
        class="line-item"
        v-for="(item, index) in shoppingCart.shoppingCart.lineItems"
        :key="item._id"
      >
        <el-col :span="2" class="centre">{{ index + 1 }}</el-col>
        <el-col :span="6">
          {{ item.name }}
          <span v-if="item.isSpecificationEnabled"
            >({{ item.specificationSelection.name }})</span
          >
          <br />
          <span class="line-item__sku">{{ item.sku }}</span>
          <div v-if="item.isDetailPageEnabled">
            <el-button
              class="bg-gold black"
              size="mini"
              @click="$router.push(`/product/${item.productId}`)"
            >
              Details
            </el-button>
          </div>
        </el-col>
        <el-col :span="4" class="centre">{{ item.unitNetPriceDisplay }}</el-col>
        <el-col :span="8" class="centre">
          <el-input-number
            class="line-item__quantity"
            size="small"
            v-model="item.quantity"
            :min="0"
            :max="
              item.isPackage ||
              item.isSpecificationEnabled ||
              item.parentProductId ||
              item.hasSubProducts
                ? 1
                : 1024
            "
            @change="(event) => onChangeQuantity(event, item)"
          ></el-input-number>
        </el-col>
        <el-col :span="4" class="centre"
          >${{ item.totalGrossPriceDisplay }}</el-col
        >
      </el-row>

      <!-- Clear soldout items dialog -->
      <el-dialog
        :visible.sync="display.dialogs.clearSoldOutItems.isVisible"
        title="Items Sold Out"
        width="80%"
      >
        <div>
          <h3>
            We're sorry, but the following items have been sold out for today.
          </h3>
          <el-row
            v-for="(item, index) of soldOutItems"
            :key="index"
            :gutter="20"
          >
            <el-col :span="12" class="right">{{ item.name }}</el-col>
            <el-col :span="12">{{ item.sku }}</el-col>
          </el-row>
          <h3>
            We've already removed the items for you. Thank you for your love to
            our food and service.
          </h3>
        </div>
        <div class="centre" slot="footer">
          <el-button
            class="bg-gold black"
            @click="display.dialogs.clearSoldOutItems.isVisible = false"
            >Okay</el-button
          >
        </div>
      </el-dialog>

      <!-- Existing same-day dine-in order addition confirmation dialog -->
      <el-dialog
        :visible.sync="display.dialogs.dineInAddLineItemsConfirmation.isVisible"
        title="Current Dine-In Order"
        width="80%"
        class="add-item-dine-in-order-dialog"
      >
        <div v-if="currentDineInOrder">
          <p class="centre break-word">
            We've detected you have an ongoing dine-in order today:
          </p>
          <h3 class="centre break-word">
            {{ currentDineInOrder.orderNumber }}
          </h3>
          <p class="centre break-word">
            {{ currentDineInOrder.createdTimeDisplayDateOnly }}
          </p>
          <p class="centre break-word">
            Do you wish us add the items to this order?
          </p>
          <div>
            <el-row
              :gutter="20"
              v-for="item of shoppingCart.shoppingCart.lineItems"
              :key="item._id"
            >
              <el-col :span="8" class="break-word">{{ item.name }}</el-col>
              <el-col :span="7" class="break-word">{{
                item.unitNetPriceDisplay
              }}</el-col>
              <el-col :span="2">{{ item.quantity }}</el-col>
              <el-col :span="7">${{ item.totalGrossPriceDisplay }}</el-col>
            </el-row>
          </div>
        </div>
        <div class="centre" slot="footer">
          <div>
            <el-button
              class="bg-black gold btn"
              @click="onClickAddToCurrentDineInOrder"
              >Add to Current Order</el-button
            >
          </div>
          <div>
            <el-button class="bg-gold black btn" @click="onClickPlaceNewOrder"
              >Place New Order</el-button
            >
          </div>
        </div>
      </el-dialog>
    </div>
    <div class="centre subtotal gold">
      Subtotal: ${{ subtotal }}
      <br />
      <delivery-indicator
        v-if="isDelivery(currentOrderMode)"
        :subtotal="Number(subtotal)"
        color="#221714"
        @clickInfo="display.dialogs.deliveryRule.isVisible = true"
      ></delivery-indicator>
    </div>
    <div class="centre">
      <el-button class="black" @click="onClickClearShoppingCart">
        Clear Cart
      </el-button>
      <el-button class="bg-gold black" @click="onClickBack">Back</el-button>
      <el-button class="bg-black gold" @click="onClickCheckout">
        Checkout
      </el-button>
    </div>

    <!-- Delivery rule dialog -->
    <delivery-rule-dialog
      :isVisible="display.dialogs.deliveryRule.isVisible"
      @close="display.dialogs.deliveryRule.isVisible = false"
    ></delivery-rule-dialog>
  </div>
</template>
<script>
import Cookies from "js-cookie";
import { mapGetters } from "vuex";
import moment, { utc } from "moment";
import { getById, addLineItems } from "@/api/order";
import logger from "@/utils/logger";
import {
  calculateSubtotal,
  setAttributeValue,
  getAttributeValue,
  deepClone,
} from "@/utils";
import { decorateShoppingCartItem } from "@/utils/shoppingCart";
import { MINIMUM_DELIVERY_AMOUNT, isDelivery } from "@/utils/order";
import { errorMessages } from "@/utils/info";

const name = "ShoppingCart";
const log = logger(name);

export default {
  name,
  data() {
    return {
      subtotal: "0.00",
      display: {
        isLoading: true,
        dialogs: {
          sideSelection: { isVisible: false },
          clearSoldOutItems: { isVisible: false },
          dineInAddLineItemsConfirmation: { isVisible: false },
          specification: { isVisible: false },
          deliveryRule: { isVisible: false },
        },
      },
      expandedItem: null,
      soldOutItems: [],
      currentDineInOrder: null,

      currentOrderMode: null,
      isDelivery,
    };
  },
  computed: {
    ...mapGetters(["shoppingCartId", "shoppingCart", "latestOrderId"]),
  },
  async created() {
    log.info("Created");
    if (!this.shoppingCart.shoppingCartId) {
      log.warn("No shopping cart ID, go home");
      return this.$router.replace("/");
    }
    log.info(`Shopping cart ID ${this.shoppingCart.shoppingCartId}`);
    await this._getShoppingCart();
    this.currentOrderMode = Cookies.get(
      "kingdynasty_ec_home_options_selected_mode"
    );
    this.display.isLoading = false;
  },
  methods: {
    onClickBack() {
      log.info("Go back");
      this.$router.go(-1);
    },
    async onClickClearShoppingCart() {
      await this.$store.dispatch("shoppingCart/clearShoppingCart");
      this.subtotal = "0.00";
      this.$message({
        message: "Shopping cart cleared.",
        type: "success",
      });
    },

    async onClickCheckout() {
      if (
        Number(this.subtotal) < MINIMUM_DELIVERY_AMOUNT &&
        isDelivery(this.currentOrderMode)
      ) {
        return this.$message(errorMessages.LESS_THAN_MINIMUM_DELIVERY_AMOUNT);
      }
      this.display.isLoading = true;
      if (this.shoppingCart.shoppingCart.lineItems.length <= 0) {
        log.info("No shopping cart item");
        this.display.isLoading = false;
        return this.$message({
          message:
            "Please consider to add some products to your shopping cart before checkout.",
          type: "error",
        });
      }
      if (this.latestOrderId) {
        log.info(
          `Latest order ${this.latestOrderId}, fetching to check if same day dine-in`
        );
        const latestOrder = await getById(this.latestOrderId);
        latestOrder.createdTimeDisplayDateOnly = moment(
          latestOrder.createdTime
        ).format("HH:mm:ss, D MMM YYYY");
        log.info("Latest order", latestOrder);
        if (this._isSameDayOngoingDineInOrder(latestOrder)) {
          log.info(
            `Latest order ${this.latestOrderId} is non-finalised dine-in`
          );
          this.currentDineInOrder = latestOrder;
          this.display.isLoading = false;
          this.display.dialogs.dineInAddLineItemsConfirmation.isVisible = true;
          return;
        }
      }
      this._goCheckout();
    },
    async onClickAddToCurrentDineInOrder() {
      this.display.isLoading = true;
      await addLineItems(
        this.currentDineInOrder._id,
        this.shoppingCart.shoppingCart._id
      );
      await this.$store.dispatch("shoppingCart/createShoppingCart");
      this.$router.push("payment");
    },
    onClickPlaceNewOrder() {
      this._goCheckout();
    },
    _isSameDayOngoingDineInOrder(order) {
      return (
        getAttributeValue(order.attributes, "mode") === "DINE_IN" &&
        order.status !== "COMPLETED" &&
        utc(order.createdTime).isSame(utc(), "day")
      );
    },
    _goCheckout() {
      log.info("Go to checkout");
      this.$router.push("order");
    },

    async onChangeQuantity(event, item) {
      // TODO change side selection
      this.display.isLoading = true;
      item.quantity = Number(event);
      this.shoppingCart.shoppingCart.lineItems =
        this.shoppingCart.shoppingCart.lineItems.filter((i) => i.quantity > 0);
      try {
        await this.$store.dispatch(
          "shoppingCart/updateShoppingCart",
          this.shoppingCart.shoppingCart
        );
        this.shoppingCart.shoppingCart.lineItems.forEach((item) => {
          decorateShoppingCartItem(item);
          this._flagItemHasSubProducts(item);
        });
        this.subtotal = calculateSubtotal(
          this.shoppingCart.shoppingCart.lineItems
        );
      } catch (err) {
        log.error(err);
        const { data, status } = err.response || {};
        if (status === 412) {
          const { parameters } = data;
          const soldOutItemsId = parameters;
          this.soldOutItems = this.shoppingCart.shoppingCart.lineItems.filter(
            (item) => soldOutItemsId.includes(item.productId)
          );
          this.shoppingCart.shoppingCart.lineItems =
            this.shoppingCart.shoppingCart.lineItems.filter(
              (item) => !soldOutItemsId.includes(item.productId)
            );
          await this.$store.dispatch(
            "shoppingCart/updateShoppingCart",
            this.shoppingCart.shoppingCart
          );
          this.shoppingCart.shoppingCart.lineItems.forEach((item) => {
            decorateShoppingCartItem(item);
            this._flagItemHasSubProducts(item);
          });
          this.subtotal = calculateSubtotal(
            this.shoppingCart.shoppingCart.lineItems
          );
          return (this.display.dialogs.clearSoldOutItems.isVisible = true);
        }
        this.$message({
          type: "error",
          message: `System error (${err.message})`,
        });
      } finally {
        this.display.isLoading = false;
      }
    },
    onClickSelectSides(item) {
      log.info("Package item for side selection", item);
      const itemCopy = deepClone(item);
      const options = itemCopy.packageOptions;
      itemCopy.packageSelections =
        itemCopy.packageSelections && itemCopy.packageSelections.length <= 0
          ? Array(itemCopy.quantity)
              .fill(0)
              .map(() =>
                options.reduce(
                  (current, incoming) =>
                    Object.assign(current, { [incoming.name]: [] }),
                  {}
                )
              )
          : itemCopy.packageSelections;
      this.expandedItem = itemCopy;
    },
    isSelectionDisabled(productSelection, productOption, lineItemSelection) {
      return (
        productSelection.isAllSelectedByDefault ||
        (!lineItemSelection[productSelection.name].includes(productOption) &&
          lineItemSelection[productSelection.name].length >=
            Number(productSelection.numberOfSelection))
      );
    },
    async onClickConfirmPackageSelection() {
      this.display.isLoading = true;
      try {
        const selections = this.expandedItem.packageSelections;
        const options = this.expandedItem.packageOptions;
        for (const opt of options) {
          if (
            selections.find(
              (sel) => sel[opt.name].length !== Number(opt.numberOfSelection)
            )
          ) {
            this.$message({
              type: "warning",
              message: "Please select for all sets.",
            });
            return;
          }
        }
        setAttributeValue(
          this.expandedItem.attributes,
          "packageSelections",
          selections
        );
        await this._updateExpandedItem();
        this.display.dialogs.sideSelection.isVisible = false;
      } catch (err) {
        log.error("Error when updating side selection", err);
        this.$message({
          type: "error",
          message: "Error when updating side selection",
        });
      } finally {
        this.display.isLoading = false;
      }
    },

    onClickSpecification(item) {
      log.info("Item for specification selection", item);
      this.expandedItem = deepClone(item);
    },
    async onClickConfirmSpecification() {
      this.display.isLoading = true;
      try {
        setAttributeValue(
          this.expandedItem.attributes,
          "specificationSelectionIndex",
          this.expandedItem.specificationSelectionIndex
        );
        setAttributeValue(
          this.expandedItem.attributes,
          "specificationSelection",
          this.expandedItem.specificationOptions[
            this.expandedItem.specificationSelectionIndex
          ]
        );
        await this._updateExpandedItem();
        this.display.dialogs.specification.isVisible = false;
      } catch (err) {
        log.error("Error when updating spec", err);
        this.$message({
          type: "error",
          message: "Error when updating specification",
        });
      } finally {
        this.display.isLoading = false;
      }
    },
    async _updateExpandedItem() {
      const itemIndex = this.shoppingCart.shoppingCart.lineItems.findIndex(
        (i) => i._id === this.expandedItem._id
      );
      const cart = this.shoppingCart.shoppingCart;
      cart.lineItems[itemIndex].attributes = this.expandedItem.attributes;
      await this.$store.dispatch("shoppingCart/updateShoppingCart", cart);
      await this._getShoppingCart();
    },

    async _getShoppingCart() {
      log.info(`Shopping cart ID ${this.shoppingCart.shoppingCartId}`);
      await this.$store.dispatch("shoppingCart/loadShoppingCart");
      this.shoppingCart.shoppingCart.lineItems.forEach((item) => {
        decorateShoppingCartItem(item);
        this._flagItemHasSubProducts(item);
      });
      this.subtotal = calculateSubtotal(
        this.shoppingCart.shoppingCart.lineItems
      );
    },
    _flagItemHasSubProducts(item) {
      if (item.parentProductId) {
        const parentItem = this.shoppingCart.shoppingCart.lineItems.find(
          (maybeParentItem) =>
            maybeParentItem.productId === item.parentProductId
        );
        if (parentItem) {
          parentItem.hasSubProducts = true;
        }
      }
    },
  },
};
</script>
<style scoped>
.line-item {
  padding: 0.5rem;
}

.line-item:not(:last-child) {
  border-bottom: 1px solid #eee;
}

.line-item-header {
  border-bottom: 2px solid #221714 !important;
}

.line-item__sku {
  font-size: 0.8rem;
}

.line-item__quantity {
  width: 100px;
}

.line-item__quantity >>> span,
.line-item__quantity >>> input {
  background: none !important;
  border-radius: 0 !important;
}

.line-item__quantity >>> span {
  background-color: #221714 !important;
}

.line-item__quantity >>> i {
  color: #c5b899 !important;
}

.subtotal {
  padding: 1.2rem;
  font-size: 1.2rem;
}

.el-checkbox >>> .el-checkbox__label,
.el-radio >>> .el-radio__label {
  color: #221714;
}

.el-checkbox >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.el-radio >>> .el-radio__input.is-checked .el-radio__inner {
  background-color: #c5b899;
  border-color: #c5b899;
}

.el-checkbox >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #dcdfe6;
}

.el-checkbox >>> .el-checkbox__input.is-checked .el-checkbox__label {
  color: #c5b899;
}

.el-input-number >>> .el-input-number__decrease.is-disabled,
.el-input-number >>> .el-input-number__increase.is-disabled {
  opacity: 0.5;
}

.side-dialog-selection >>> label.el-checkbox,
.side-dialog-selection >>> label.el-radio {
  display: block;
  margin-bottom: 0.5rem;
}

.side-dialog-selection >>> .el-checkbox__label {
  white-space: normal;
}

.add-item-dine-in-order-dialog .btn {
  max-width: 12rem;
  width: 100%;
  margin-top: 0.6rem;
}

.side-dialog-selection .el-radio-group {
  width: 100%;
}

.side-dialog-selection .el-radio-group >>> .el-radio__label {
  display: inline-block;
  white-space: normal;
  line-height: 1.2rem;
}
</style>